import { DataTable, type DataTableColumn, Select, Toggle, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  adminGetWriteActionPolicyOptions,
  adminGetWriteActionPolicyQueryKey,
  adminUpdateWriteActionPolicyMutation,
  errorMessage,
  type WriteActionPolicyUpdate,
  type WriteActionSource,
} from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";

type Level = WriteActionSource["level"];
const LEVELS: Level[] = ["ask", "chat_only", "off"];

/**
 * Write actions (MHS-973): whether this organization allows them at all (off until an admin turns them on), the level
 * for sources not set yet (including connectors people add later), and for each connector and builtin tool that can change things outside AnthmAI, whether it asks
 * each time, works in chat only (not in workflows), or is off; and whether people may approve an action for the rest
 * of a chat, or always allow one in a workflow. Each change saves on its own.
 */
export function WriteActionsPolicySection({ canEdit }: Readonly<{ canEdit: boolean }>) {
  const { t } = useTranslation("adminIntegrations", { keyPrefix: "writeActions" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const policy = useQuery({ ...adminGetWriteActionPolicyOptions(), select: (r) => r.data });
  const save = useMutation({
    ...adminUpdateWriteActionPolicyMutation(),
    onSuccess: (res) => {
      queryClient.setQueryData(adminGetWriteActionPolicyQueryKey(), res);
      toast.success(t("saved"));
    },
    onError: (error) => toast.error(errorMessage(error, t("saveFailed"))),
  });
  const update = (body: WriteActionPolicyUpdate) => save.mutate({ body });
  const disabled = !canEdit || save.isPending;

  const columns: DataTableColumn<WriteActionSource>[] = [
    {
      key: "source",
      header: t("columns.source"),
      mobile: "primary",
      render: (row) => (
        <div className="flex flex-col">
          <Text as="span" variant="body-sm">
            {row.label}
          </Text>
          <Text as="span" variant="caption" tone="tertiary">
            {t(`kind.${row.kind}`)}
          </Text>
        </div>
      ),
    },
    {
      key: "level",
      header: t("columns.level"),
      render: (row) => (
        <div className="flex flex-col gap-1">
          <Select
            aria-label={t("levelFor", { source: row.label })}
            value={row.level}
            disabled={disabled}
            options={LEVELS.map((level) => ({ value: level, label: t(`level.${level}`) }))}
            onChange={(next) => update({ levels: { [row.key]: next as Level } })}
          />
          {!row.isSet && (
            <Text as="span" variant="caption" tone="tertiary">
              {t("followsDefault")}
            </Text>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageSection title={t("title")} description={t("description")}>
      {policy.isError && (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      )}
      {policy.data && (
        <div className="flex flex-col gap-4">
          <Switch
            label={t("enabled")}
            help={policy.data.enabled ? t("enabledHelpOn") : t("enabledHelpOff")}
            checked={policy.data.enabled}
            disabled={disabled}
            onChange={(checked) => update({ enabled: checked })}
          />
          {policy.data.enabled && (
            <>
              <div className="flex max-w-sm flex-col gap-1">
                <Text as="span" variant="body-sm">
                  {t("defaultLevel")}
                </Text>
                <Select
                  aria-label={t("defaultLevel")}
                  value={policy.data.defaultLevel}
                  disabled={disabled}
                  options={LEVELS.map((level) => ({ value: level, label: t(`level.${level}`) }))}
                  onChange={(next) => update({ defaultLevel: next as Level })}
                />
                <Text variant="caption" tone="tertiary">
                  {t("defaultLevelHelp")}
                </Text>
              </div>
              <Switch
                label={t("allowRemember")}
                help={t("allowRememberHelp")}
                checked={policy.data.allowRemember}
                disabled={disabled}
                onChange={(checked) => update({ allowRemember: checked })}
              />
              <Switch
                label={t("allowPreapproval")}
                help={t("allowPreapprovalHelp")}
                checked={policy.data.allowPreapproval}
                disabled={disabled}
                onChange={(checked) => update({ allowPreapproval: checked })}
              />
              <DataTable
                aria-label={t("title")}
                columns={columns}
                rows={policy.data.sources ?? []}
                rowKey={(row) => row.key}
                empty={
                  <Text variant="body-sm" tone="tertiary">
                    {t("empty")}
                  </Text>
                }
              />
            </>
          )}
        </div>
      )}
    </PageSection>
  );
}

function Switch({
  label,
  help,
  checked,
  disabled,
  onChange,
}: Readonly<{
  label: string;
  help: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}>) {
  return (
    <div className="flex flex-col gap-1">
      <Toggle label={label} checked={checked} disabled={disabled} onChange={onChange} />
      <Text variant="caption" tone="tertiary">
        {help}
      </Text>
    </div>
  );
}
